import { RequestState } from '@modules/registration/omc-registration/store/omc-registration.action';
import { createReducer, on } from '@ngrx/store';
import * as FinancialSecurityActions from './financial-security-configuration.actions';
import { handleFailure, handleRequest, handleSuccess } from '@shared/ngrx-factory/factory.reducer';

export interface FinancialSecurityState {
  securityTypes: RequestState<any>;
  securityRules: RequestState<any>;
  currencies: RequestState<any>;
  bankingIntegrations?: RequestState<any>;
  fobReferences?: RequestState<any>;
  coverageFormulas?: RequestState<any>;
  peggedCostCategories: RequestState<any>;
  bankGuaranteeTypes: RequestState<any>;
}

export const initialFinancialSecurityState: FinancialSecurityState = {
  securityTypes: { loading: false, message: null, response: null, error: null },
  securityRules: { loading: false, message: null, response: null, error: null },
  currencies: { loading: false, message: null, response: null, error: null },
  bankingIntegrations: { loading: false, message: null, response: null, error: null },
  fobReferences: { loading: false, message: null, response: null, error: null },
  coverageFormulas: { loading: false, message: null, response: null, error: null },
  peggedCostCategories: { loading: false, message: null, response: null, error: null },
  bankGuaranteeTypes: { loading: false, message: null, response: null, error: null },
};

export const financialSecurityReducer = createReducer(
  initialFinancialSecurityState,

  // Security Types
  on(FinancialSecurityActions.FinancialSecurityTypesAction.request, (state) =>
    handleRequest(state, 'securityTypes'),
  ),

  on(FinancialSecurityActions.FinancialSecurityTypesAction.success, (state, { response }) =>
    handleSuccess(state, 'securityTypes', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityTypesAction.failure, (state, { error }) =>
    handleFailure(state, 'securityTypes', error),
  ),

  on(FinancialSecurityActions.FinancialSecuritySubmitTypesAction.request, (state) =>
    handleRequest(state, 'securityTypes'),
  ),

  on(FinancialSecurityActions.FinancialSecuritySubmitTypesAction.success, (state, { response }) =>
    handleSuccess(state, 'securityTypes', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecuritySubmitTypesAction.failure, (state, { error }) =>
    handleFailure(state, 'securityTypes', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityTypesUpdateAction.request, (state) =>
    handleRequest(state, 'securityTypes'),
  ),

  on(FinancialSecurityActions.FinancialSecurityTypesUpdateAction.success, (state, { response }) =>
    handleSuccess(state, 'securityTypes', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityTypesUpdateAction.failure, (state, { error }) =>
    handleFailure(state, 'securityTypes', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityTypesDeleteAction.request, (state) =>
    handleRequest(state, 'securityTypes'),
  ),

  on(FinancialSecurityActions.FinancialSecurityTypesDeleteAction.success, (state, { response }) =>
    handleSuccess(state, 'securityTypes', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityTypesDeleteAction.failure, (state, { error }) =>
    handleFailure(state, 'securityTypes', error),
  ),

  // Security Rules
  on(FinancialSecurityActions.FinancialSecurityRulesAction.request, (state) =>
    handleRequest(state, 'securityRules'),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesAction.success, (state, { response }) =>
    handleSuccess(state, 'securityRules', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesAction.failure, (state, { error }) =>
    handleFailure(state, 'securityRules', error),
  ),

  on(FinancialSecurityActions.FinancialSecuritySubmitRulesAction.request, (state) =>
    handleRequest(state, 'securityRules'),
  ),

  on(FinancialSecurityActions.FinancialSecuritySubmitRulesAction.success, (state, { response }) =>
    handleSuccess(state, 'securityRules', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecuritySubmitRulesAction.failure, (state, { error }) =>
    handleFailure(state, 'securityRules', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesUpdateAction.request, (state) =>
    handleRequest(state, 'securityRules'),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesUpdateAction.success, (state, { response }) =>
    handleSuccess(state, 'securityRules', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesUpdateAction.failure, (state, { error }) =>
    handleFailure(state, 'securityRules', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesDeleteAction.request, (state) =>
    handleRequest(state, 'securityRules'),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesDeleteAction.success, (state, { response }) =>
    handleSuccess(state, 'securityRules', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesDeleteAction.failure, (state, { error }) =>
    handleFailure(state, 'securityRules', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesActivateAction.request, (state) =>
    handleRequest(state, 'securityRules'),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesActivateAction.success, (state, { response }) =>
    handleSuccess(state, 'securityRules', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesActivateAction.failure, (state, { error }) =>
    handleFailure(state, 'securityRules', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesDeactivateAction.request, (state) =>
    handleRequest(state, 'securityRules'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityRulesDeactivateAction.success,
    (state, { response }) => handleSuccess(state, 'securityRules', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityRulesDeactivateAction.failure, (state, { error }) =>
    handleFailure(state, 'securityRules', error),
  ),

  // Currencies
  on(FinancialSecurityActions.FinancialSecurityCurrenciesAction.request, (state) =>
    handleRequest(state, 'currencies'),
  ),

  on(FinancialSecurityActions.FinancialSecurityCurrenciesAction.success, (state, { response }) =>
    handleSuccess(state, 'currencies', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityCurrenciesAction.failure, (state, { error }) =>
    handleFailure(state, 'currencies', error),
  ),

  // Banking Configuration
  on(FinancialSecurityActions.FinancialSecurityBankingIntegrationsAction.request, (state) =>
    handleRequest(state, 'bankingIntegrations'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankingIntegrationsAction.success,
    (state, { response }) => handleSuccess(state, 'bankingIntegrations', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankingIntegrationsAction.failure,
    (state, { error }) => handleFailure(state, 'bankingIntegrations', error),
  ),

  // Banking Configuration - SUBMIT
  on(FinancialSecurityActions.FinancialSecurityBankingIntegrationsSubmitAction.request, (state) =>
    handleRequest(state, 'bankingIntegrations'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankingIntegrationsSubmitAction.success,
    (state, { response }) => handleSuccess(state, 'bankingIntegrations', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankingIntegrationsSubmitAction.failure,
    (state, { error }) => handleFailure(state, 'bankingIntegrations', error),
  ),

  // Banking Configuration - UPDATE
  on(FinancialSecurityActions.FinancialSecurityBankingIntegrationsUpdateAction.request, (state) =>
    handleRequest(state, 'bankingIntegrations'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankingIntegrationsUpdateAction.success,
    (state, { response }) => handleSuccess(state, 'bankingIntegrations', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankingIntegrationsUpdateAction.failure,
    (state, { error }) => handleFailure(state, 'bankingIntegrations', error),
  ),

  // Countries
  on(FinancialSecurityActions.FinancialSecurityCurrenciesAction.request, (state) =>
    handleRequest(state, 'currencies'),
  ),

  on(FinancialSecurityActions.FinancialSecurityCurrenciesAction.success, (state, { response }) =>
    handleSuccess(state, 'currencies', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityCurrenciesAction.failure, (state, { error }) =>
    handleFailure(state, 'currencies', error),
  ),

  // FOB References
  on(FinancialSecurityActions.FinancialSecurityFobReferencesAction.request, (state) =>
    handleRequest(state, 'fobReferences'),
  ),

  on(FinancialSecurityActions.FinancialSecurityFobReferencesAction.success, (state, { response }) =>
    handleSuccess(state, 'fobReferences', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityFobReferencesAction.failure, (state, { error }) =>
    handleFailure(state, 'fobReferences', error),
  ),

  on(FinancialSecurityActions.FinancialSecuritySubmitFobReferencesAction.request, (state) =>
    handleRequest(state, 'fobReferences'),
  ),

  on(
    FinancialSecurityActions.FinancialSecuritySubmitFobReferencesAction.success,
    (state, { response }) => handleSuccess(state, 'fobReferences', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecuritySubmitFobReferencesAction.failure,
    (state, { error }) => handleFailure(state, 'fobReferences', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityFobReferencesUpdateAction.request, (state) =>
    handleRequest(state, 'fobReferences'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityFobReferencesUpdateAction.success,
    (state, { response }) => handleSuccess(state, 'fobReferences', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityFobReferencesUpdateAction.failure,
    (state, { error }) => handleFailure(state, 'fobReferences', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityFobReferencesDeleteAction.request, (state) =>
    handleRequest(state, 'fobReferences'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityFobReferencesDeleteAction.success,
    (state, { response }) => handleSuccess(state, 'fobReferences', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityFobReferencesDeleteAction.failure,
    (state, { error }) => handleFailure(state, 'fobReferences', error),
  ),

  // Coverage Formulas
  on(FinancialSecurityActions.FinancialSecurityCoverageFormulaAction.request, (state) =>
    handleRequest(state, 'coverageFormulas'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityCoverageFormulaAction.success,
    (state, { response }) => handleSuccess(state, 'coverageFormulas', response.data),
  ),

  on(FinancialSecurityActions.FinancialSecurityCoverageFormulaAction.failure, (state, { error }) =>
    handleFailure(state, 'coverageFormulas', error),
  ),

  on(FinancialSecurityActions.FinancialSecuritySubmitCoverageFormulaAction.request, (state) =>
    handleRequest(state, 'coverageFormulas'),
  ),

  on(
    FinancialSecurityActions.FinancialSecuritySubmitCoverageFormulaAction.success,
    (state, { response }) => handleSuccess(state, 'coverageFormulas', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecuritySubmitCoverageFormulaAction.failure,
    (state, { error }) => handleFailure(state, 'coverageFormulas', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityCoverageFormulaUpdateAction.request, (state) =>
    handleRequest(state, 'coverageFormulas'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityCoverageFormulaUpdateAction.success,
    (state, { response }) => handleSuccess(state, 'coverageFormulas', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityCoverageFormulaUpdateAction.failure,
    (state, { error }) => handleFailure(state, 'coverageFormulas', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityCoverageFormulaDeleteAction.request, (state) =>
    handleRequest(state, 'coverageFormulas'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityCoverageFormulaDeleteAction.success,
    (state, { response }) => handleSuccess(state, 'coverageFormulas', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityCoverageFormulaDeleteAction.failure,
    (state, { error }) => handleFailure(state, 'coverageFormulas', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityCoverageFormulaActivateAction.request, (state) =>
    handleRequest(state, 'coverageFormulas'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityCoverageFormulaActivateAction.success,
    (state, { response }) => handleSuccess(state, 'coverageFormulas', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityCoverageFormulaActivateAction.failure,
    (state, { error }) => handleFailure(state, 'coverageFormulas', error),
  ),

  //Pegged Cost Categories
  on(FinancialSecurityActions.FinancialSecurityPeggedCostCategoriesAction.request, (state) =>
    handleRequest(state, 'peggedCostCategories'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityPeggedCostCategoriesAction.success,
    (state, { response }) => handleSuccess(state, 'peggedCostCategories', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityPeggedCostCategoriesAction.failure,
    (state, { error }) => handleFailure(state, 'peggedCostCategories', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityPeggedCostCategorySubmitAction.request, (state) =>
    handleRequest(state, 'peggedCostCategories'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityPeggedCostCategorySubmitAction.success,
    (state, { response }) => handleSuccess(state, 'peggedCostCategories', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityPeggedCostCategorySubmitAction.failure,
    (state, { error }) => handleFailure(state, 'peggedCostCategories', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityPeggedCostCategoryUpdateAction.request, (state) =>
    handleRequest(state, 'peggedCostCategories'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityPeggedCostCategoryUpdateAction.success,
    (state, { response }) => handleSuccess(state, 'peggedCostCategories', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityPeggedCostCategoryUpdateAction.failure,
    (state, { error }) => handleFailure(state, 'peggedCostCategories', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityPeggedCostCategoryDeleteAction.request, (state) =>
    handleRequest(state, 'peggedCostCategories'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityPeggedCostCategoryDeleteAction.success,
    (state, { response }) => handleSuccess(state, 'peggedCostCategories', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityPeggedCostCategoryDeleteAction.failure,
    (state, { error }) => handleFailure(state, 'peggedCostCategories', error),
  ),

  // Bank Guarantee Types
  on(FinancialSecurityActions.FinancialSecurityBankGuaranteeTypesAction.request, (state) =>
    handleRequest(state, 'bankGuaranteeTypes'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankGuaranteeTypesAction.success,
    (state, { response }) => handleSuccess(state, 'bankGuaranteeTypes', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankGuaranteeTypesAction.failure,
    (state, { error }) => handleFailure(state, 'bankGuaranteeTypes', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeSubmitAction.request, (state) =>
    handleRequest(state, 'bankGuaranteeTypes'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeSubmitAction.success,
    (state, { response }) => handleSuccess(state, 'bankGuaranteeTypes', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeSubmitAction.failure,
    (state, { error }) => handleFailure(state, 'bankGuaranteeTypes', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeUpdateAction.request, (state) =>
    handleRequest(state, 'bankGuaranteeTypes'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeUpdateAction.success,
    (state, { response }) => handleSuccess(state, 'bankGuaranteeTypes', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeUpdateAction.failure,
    (state, { error }) => handleFailure(state, 'bankGuaranteeTypes', error),
  ),

  on(FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeDeleteAction.request, (state) =>
    handleRequest(state, 'bankGuaranteeTypes'),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeDeleteAction.success,
    (state, { response }) => handleSuccess(state, 'bankGuaranteeTypes', response.data),
  ),

  on(
    FinancialSecurityActions.FinancialSecurityBankGuaranteeTypeDeleteAction.failure,
    (state, { error }) => handleFailure(state, 'bankGuaranteeTypes', error),
  ),
);
